import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Tag,
  CurrencyDollar,
  IdentificationCard,
  PlusCircle,
  PlayCircle,
  Info,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import API from '../../../../../api/api'
import FeaturedTable from '../../../../FeaturedTable/FeaturedTable'
import SpaceModal from '../modals/SpaceModal'
import VideoPreviewModal from '../../../../VideoPreviewModal/VideoPreviewModal'
import SuccessConfirmationModal from '../../../../Popups/SuccessConfirmationModal'
import { spacesColumns } from '../helpers'
import { selectActiveBuilding } from '../../../../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'

const Spaces = ({ amenity = null }) => {
  const { t } = useTranslation(['reservations'])
  const { amenityId } = useParams()
  const { displaySnackbar } = useSnackbar()
  const [locations, setLocations] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [showSpaceModal, setShowSpaceModal] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const building = useSelector(selectActiveBuilding)
  const columns = spacesColumns(
    {
      onEdit: (location) => {
        setSelectedLocation(location)
        setShowSpaceModal(true)
      },
    },
    t,
  )

  const handleAddSpace = () => {
    setShowSpaceModal(true)
  }

  const handleSendEmail = async () => {
    try {
      setIsLoading(true)
      await API.sendUpsellingMessage({ type: 'amenityLocationAvailable' }, building.id)
      setIsConfirmationVisible(true)
    } catch {
      displaySnackbar(t('reservations:amenityForms.spaces.errorOccurredTryAgain'), true)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchLocations = async () => {
    try {
      setIsLoading(true)
      const { data } = await API.getAmenityLocations(amenityId)
      const locations = data?.locations || []
      setLocations(locations)
    } catch (error) {
      console.error('Error fetching locations', error)
    } finally {
      setIsLoading(false)
    }
  }

  const onCloseSpaceModal = () => {
    setShowSpaceModal(false)
    setSelectedLocation(null)
  }

  useEffect(() => {
    if (building?.amenityLocationAvailable) {
      fetchLocations()
    }
  }, [amenityId, building.amenityLocationAvailable])

  return (
    <div className={`form-step ${isLoading ? 'is-loading' : ''}`}>
      {locations.length > 0 && building.amenityLocationAvailable ? (
        <div className="form-step__content">
          <div className="form-step__column">
            <div className="spaces__info-banner">
              <div className="spaces__info-banner-content">
                <div className="spaces__info-banner-left">
                  <Info size={18} />
                  <div className="spaces__info-text">
                    <div className="spaces__info-title">
                      {t('reservations:amenityForms.spaces.infoBannerTitle')}
                    </div>
                    <div className="spaces__info-subtitle">
                      {t('reservations:amenityForms.spaces.infoBannerSubtitle')}
                    </div>
                  </div>
                </div>
                <div className="spaces__info-banner-right">
                  <button
                    type="button"
                    className="spaces__tutorial-button"
                    onClick={() => setShowVideoModal(true)}
                  >
                    <PlayCircle size={16} />
                    {t('reservations:amenityForms.details.watchTutorial')}
                  </button>
                </div>
              </div>
            </div>

            <div className="spaces__table-wrapper">
              <div className="spaces__table-header">
                <button type="button" className="spaces__add-button" onClick={handleAddSpace}>
                  <PlusCircle size={24} />
                  {t('reservations:amenityForms.spaces.addASpace')}
                </button>
              </div>

              <div className="spaces__table-container">
                <FeaturedTable columns={columns} data={locations} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="form-step__content form-step__content--centered">
          <div className="form-step__section">
            <div className="form-step__column">
              <div className="spaces__title">{t('reservations:amenityForms.spaces.title')}</div>
              <div className="spaces__subtitle">
                {t('reservations:amenityForms.spaces.subtitle')}
              </div>
            </div>

            <div className="spaces__cards">
              <div className="spaces__card">
                <div className="spaces__card-title">
                  {' '}
                  <div className="spaces__card-icon">
                    <Tag size={18} style={{ rotate: '90deg' }} />
                  </div>
                  {t('reservations:amenityForms.spaces.addVisuals')}
                </div>
                <div className="spaces__card-desc">
                  {t('reservations:amenityForms.spaces.addVisualsDesc')}
                </div>
              </div>
              <div className="spaces__card">
                <div className="spaces__card-title">
                  {' '}
                  <div className="spaces__card-icon">
                    <IdentificationCard size={18} />
                  </div>
                  {t('reservations:amenityForms.spaces.nameAndDescribe')}
                </div>
                <div className="spaces__card-desc">
                  {t('reservations:amenityForms.spaces.nameAndDescribeDesc')}
                </div>
              </div>
              <div className="spaces__card">
                <div className="spaces__card-title">
                  {' '}
                  <div className="spaces__card-icon">
                    <CurrencyDollar size={18} />
                  </div>
                  {t('reservations:amenityForms.spaces.setAvailability')}
                </div>
                <div className="spaces__card-desc">
                  {t('reservations:amenityForms.spaces.setAvailabilityDesc')}
                </div>
              </div>
            </div>

            <div className="form-step__row">
              {building.amenityLocationAvailable ? (
                <button type="button" className="button button-black" onClick={handleAddSpace}>
                  <PlusCircle size={16} />
                  {t('reservations:amenityForms.spaces.addASpace')}
                </button>
              ) : (
                <button type="button" className="button button-black" onClick={handleSendEmail}>
                  {t('reservations:amenityForms.spaces.talkToAccountManager')}
                </button>
              )}
            </div>
          </div>

          <div className="form-step__section">
            <div className="spaces__hero">
              <img
                src="/images/upselling/amenity-spaces.png"
                alt={t('reservations:amenityForms.spaces.spacesOverviewAlt')}
              />
              <button
                type="button"
                className="spaces__hero-play"
                aria-label={t('reservations:amenityForms.spaces.watchOverviewAriaLabel')}
                onClick={() => setShowVideoModal(true)}
              >
                <PlayCircle size={64} weight="fill" />
              </button>
            </div>
          </div>
        </div>
      )}
      {showSpaceModal && (
        <SpaceModal
          onClose={onCloseSpaceModal}
          onSuccess={fetchLocations}
          selectedLocation={selectedLocation}
          prefillData={
            !selectedLocation && locations.length === 0 && amenity?.hasReservations
              ? {
                  name: amenity.name,
                  description: amenity.description,
                  image: amenity.image,
                }
              : null
          }
        />
      )}
      {showVideoModal && (
        <VideoPreviewModal
          handleCloseVideo={() => setShowVideoModal(false)}
          videoUrl="https://d3b2rqyxkxi2am.cloudfront.net/videos/amenity-spaces.mp4"
        />
      )}
      {isConfirmationVisible && (
        <SuccessConfirmationModal
          header={t('reservations:amenityMarketplaceModal.successTitle')}
          description={t('reservations:amenityMarketplaceModal.successDescription')}
          handleClose={() => setIsConfirmationVisible(false)}
        />
      )}
    </div>
  )
}

Spaces.propTypes = {
  amenity: PropTypes.object,
}

export default Spaces
